/**
 * MessageFeedbackWrapper Component
 *
 * Wrapper component that integrates our custom feedback system with @assistant-ui/react's
 * message context. Extracts message ID and handles integration between built-in and custom
 * feedback systems.
 */

import React from 'react';
import { useMessage, ThreadPrimitive } from '@assistant-ui/react';
import { Feedback } from './feedback';
import {
  shouldShowFeedbackButtons,
  shouldButtonsBeInteractive,
  hasFeedbackSelected,
} from '../../utils/feedbackMetadata';
import { useFeedback } from '../../hooks/useFeedback';

interface MessageFeedbackWrapperProps {
  /** Whether to disable the feedback buttons */
  disabled?: boolean;

  /** Additional CSS class name */
  className?: string;
}

export const MessageFeedbackWrapper: React.FC<MessageFeedbackWrapperProps> = ({
  disabled = false,
  className,
}) => {
  const message = useMessage();

  const { messageId: activeFeedbackMessageId } = useFeedback();

  // Use new visibility logic: show for last message OR messages with feedback selected
  if (
    !shouldShowFeedbackButtons(message, activeFeedbackMessageId ?? undefined)
  ) {
    return null;
  }

  const isInteractive = shouldButtonsBeInteractive(message);
  const hasSubmittedFeedback = message.submittedFeedback !== undefined;
  const messageFeedbackSelected = hasFeedbackSelected(message);

  // Messages with feedback already selected should always show buttons
  // even when thread is running (prevents flicker during new message generation)
  // Messages without feedback should only show when thread is not running
  if (messageFeedbackSelected) {
    // Always show for messages with feedback selected
    return (
      <div className={className}>
        <Feedback
          messageId={message.id}
          disabled={disabled || hasSubmittedFeedback || !isInteractive}
        />
      </div>
    );
  }

  // For messages without feedback, only show when thread is not running
  return (
    <div className={className}>
      <ThreadPrimitive.If running={false}>
        <Feedback
          messageId={message.id}
          disabled={disabled || hasSubmittedFeedback || !isInteractive}
        />
      </ThreadPrimitive.If>
    </div>
  );
};
